import { FormGroupHeader } from "@/components/shared";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { BidChatBubble, ChatInputBar, PendingDecisionBar, BidDecisionOverruleDialog } from "./Components";
import { useTranslation } from "react-i18next";
import { BidDecision, DecisionEnum, BidDecisionGroup, TenderState, DecisionActionEnum, FetchUsersOnTenderQuery, FetchUsersOnTenderQueryVariables, UserOnEntity, BidDecisionGroupPermissionEnum, FetchUsersOnTenderDocument } from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";
import { useRef, useEffect, JSX } from "react";
import { useParams } from "react-router-dom";
import { useBidDecisionGroupData } from "@/hooks/cache";
import { useFetchBidDecisionsByGroupPolling } from "@/hooks/queries";
import useUserStore from "@/store/shield";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CircleLoader } from "@/components/shared/loaders";
import { cn } from "@/lib/utils";

/**
 * BidDecisionsComponent is a React functional component that renders the bidding decision screen.
 * It fetches bid decisions by group and displays them in a chat-like interface.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <BidDecisionsComponent />
 *
 * @hook
 * - `useTranslation` - For internationalization.
 * - `useParams` - To get the bid decision group ID from the URL.
 * - `useFetchBidDecisionsByGroupQuery` - To fetch bid decisions by group.
 * - `useBidDecisionGroupData` - To fetch bid decision group data from cache.
 * - `useEffect` - To scroll to the bottom of the chat area when the component loads or bid decisions data changes.
 *
 * @returns {JSX.Element} The rendered component.
 */
const BidDecisionsComponent = (): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components"]);
  const scrollEndRef = useRef<HTMLDivElement>(null);
  const { bidDecisionGroupID, tenderID } = useParams<{ bidDecisionGroupID: string, tenderID: string }>();

  // Queries
  const { data, loading, useRefetch, stopPolling } = useFetchBidDecisionsByGroupPolling(bidDecisionGroupID as string, tenderID as string);
  const bidDecisionGroupData = useBidDecisionGroupData({ id: bidDecisionGroupID as string });
  const { data: usersOnTenderData } = useQuery<FetchUsersOnTenderQuery, FetchUsersOnTenderQueryVariables>(FetchUsersOnTenderDocument, {
    variables: {
      tenderId: Number(tenderID)
    }
  });

  // Constants
  const tenderUsers = usersOnTenderData?.fetchUsersOnTender || [];
  const userId = useUserStore((state) => state.userId);
  const hasFinalDecision = bidDecisionGroupData?.awaitingOrganizationId === null;
  const bidDecisionsLength = data?.fetchBidDecisionsByGroup?.bidDecisions?.length;
  const groupPermissions = bidDecisionGroupData?.permissions;
  const canOverrule = groupPermissions?.includes(BidDecisionGroupPermissionEnum.Overrule);
  const isAutoApproved = bidDecisionGroupData?.autoApproved;
  const userHasPermission = [
    BidDecisionGroupPermissionEnum.Decide,
    BidDecisionGroupPermissionEnum.DecideAheadOfTurn,
    BidDecisionGroupPermissionEnum.Overrule,
    BidDecisionGroupPermissionEnum.Comment
  ].some(permission => groupPermissions?.includes(permission));

  // Stop polling if a uninamous decision has been reached
  useEffect(() => {
    if(hasFinalDecision) stopPolling();
  }, [hasFinalDecision]);

  /**
   * Aligns the chat bubble based on the bid decision's action and type.
   *
   * @function alignChatBubble
   * @param {BidDecision} bidDecision - The bid decision to evaluate.
   * @returns {string} - The CSS alignment class for the chat bubble.
   */
  const alignChatBubble = (bidDecision: BidDecision): string => {
    // Align to end if the current user made the comment
    if(bidDecision.action === DecisionActionEnum.Comment &&
      bidDecision.decidedBy === userId
    ) {
      return "justify-end";
    }
    else if (bidDecision.decision === DecisionEnum.Bid && bidDecision.action !== DecisionActionEnum.Comment) return "justify-end";
    else return "";
  };

  useEffect(() => {
    // Scroll to bottom when component loads
    if (scrollEndRef.current) {
      scrollEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [data?.fetchBidDecisionsByGroup]); // Run effect whenever the bid decisions data changes

  const actionButtons: ActionButtonConfig[] = [
    {
      text: <ArrowPathIcon className="icon-sm text-gray-800 group-hover:animate-spin"/>,
      variant: "outline",
      size: "sm",
      className: "group active:scale-95",
      actionFn: () => {useRefetch(data?.fetchBidDecisionsByGroup.tenderState as TenderState);},
    },
  ];

  return (
    <div className="flex flex-col gap-2 h-full pb-[100px]">
      {/* Main Bidding Decision Screen */}
      <FormGroupHeader
        className="mb-0 flex-none pr-4 pl-2"
        title={t("chatwindow.title")}
        actionButtons={actionButtons}
      >
        {canOverrule ? <BidDecisionOverruleDialog hasFinalDecision={hasFinalDecision} /> : undefined}
      </FormGroupHeader>
      <ScrollArea className="grow pr-4 pb-8 pl-2 min-h-0">
        <div className="space-y-4 h-full">
          {bidDecisionsLength && bidDecisionsLength > 0 ? (
            <>
              {data?.fetchBidDecisionsByGroup.bidDecisions.map((bidDecision, _index) => {
                // Find the first non-comment decision in the list
                const firstNonCommentIndex = data?.fetchBidDecisionsByGroup.bidDecisions.findIndex(
                  decision => decision.action !== DecisionActionEnum.Comment
                ) ?? -1;
                const isInitialDecision = firstNonCommentIndex === _index;

                return (
                  <div key={`bid-decision-${bidDecision.id}`} className={cn("flex", alignChatBubble(bidDecision as unknown as BidDecision))}>
                    <BidChatBubble
                      isFirstItem={isInitialDecision}
                      bidDecision={bidDecision as unknown as BidDecision}
                      tenderUsers={tenderUsers as UserOnEntity[]}
                    />
                  </div>
                );
              })}
              {/* div for anchor to auto scroll to bottom */}
              <div ref={scrollEndRef} />
            </>
          ) : loading ? (
            <div className="flex items-center justify-center h-[50vh]">
              <CircleLoader
                visible={true}
                height={80}
                width={80}
                strokeWidth={8}
              />
            </div>
          ) : isAutoApproved ? (
            <div className="flex items-center justify-center h-[50vh]">
              <p className="text-sm text-gray-400">
                {t("chatwindow.autoapproved")}
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[50vh] ">
              <p className="text-sm text-gray-400">
                {t("chatwindow.nodata")}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
      <div className="space-y-2 pr-4 pl-2">
        {!loading && (
          <>
            <PendingDecisionBar
              bidDecisions={data?.fetchBidDecisionsByGroup.bidDecisions as BidDecision[] ?? []}
              bidDecisionGroup={bidDecisionGroupData as BidDecisionGroup}
              isAutoApproved={isAutoApproved as boolean}
            />
            {
              !hasFinalDecision && (
                <ChatInputBar
                  tenderState={data?.fetchBidDecisionsByGroup.tenderState as TenderState}
                  bidDecisionGroup={bidDecisionGroupData as BidDecisionGroup}
                  bidDecisions={data?.fetchBidDecisionsByGroup.bidDecisions as BidDecision[] ?? []}
                  userHasPermission={userHasPermission ?? false}
                  tenderUsers={tenderUsers as UserOnEntity[]}
                  hasFinalDecision={hasFinalDecision}
                />
              )
            }
          </>
        )}
      </div>
    </div>
  );
};

export default BidDecisionsComponent;
